/**
 * credentialStatus.ts — shared credential status utilities for the admin
 * Credentials surfaces (list + edit page).
 *
 * This is the FE-side of the canonical 6-state mapping described in:
 *   TDD §"Canonical 6-State Mapping"
 *   Brand guide §38
 *
 * The derivation logic covers the 4 server-emittable verdict states from the
 * live probe map, extended with two new display states:
 *   - `not_tested_yet` (rule 2 — server-authoritative, no row in cache)
 *   - `testing`        (rule 1 — FE-only in-flight state)
 *
 * Both the list `CredentialStatusBadge` and the edit `CredentialStatusCard`
 * import from here — single source of truth for all 6 states.
 *
 * Dashboard-era only. Never call these for workflow-only credentials.
 *
 * Slice 3.1 — credential-status Epic 3, 2026-05-30.
 */

import type { CredentialStatusState } from '@/types/credentials'
import type { DashboardDomainAvailability } from '@/types/DashboardDomainRegistry.types'
import type { CredentialCapabilityAvailability } from '@/types/DashboardV1.types'

// ─── 6-state token ───────────────────────────────────────────────────────────

/**
 * All 6 display states (5 server-emittable + 1 FE-only in-flight).
 * `CredentialStatusState` (wire type) covers the 5 server states.
 * `DisplayStatusState` adds `testing` for local in-flight use.
 */
export type DisplayStatusState = CredentialStatusState | 'testing'

// ─── Derivation from live probe map (post-Test action) ───────────────────────

/**
 * deriveStatusFromPerDomainMap — maps the raw per-domain probe response to one
 * of the 6 display states.
 *
 * Called ONLY after a live "Test connection" probe returns (in-flight → resolved).
 * For resting display, read `credential.connectionStatus.state` directly (server
 * already ran this derivation via `deriveCredentialStatusState` at read time).
 *
 * Rules (ordered; first match wins):
 * 1. Exclude `not_implemented` stub domains from consideration.
 * 2. Any considered domain `state === 'available'`                  → 'valid'
 * 3. Any `state === 'unavailable'` + `reason === 'auth_failed'`     → 'invalid_key'
 * 4. Any `state === 'unavailable'` + `reason === 'access_denied'`   → 'missing_permissions'
 * 5. All remaining (unknown / transient / cold_cache / empty map)   → 'couldnt_verify'
 *
 * Note: `not_tested_yet` is never returned here — a probe that returns an empty
 * or null map falls to rule 5 (`couldnt_verify`), which is correct: the probe
 * ran but produced no usable verdict.
 *
 * The `testing` state is managed externally (component local state); it is never
 * derived from the map.
 */
export function deriveStatusFromPerDomainMap(
  perDomain:
    | Record<string, DashboardDomainAvailability>
    | Record<string, CredentialCapabilityAvailability>
    | null
    | undefined,
): Exclude<DisplayStatusState, 'not_tested_yet' | 'testing'> {
  if (!perDomain || typeof perDomain !== 'object') {
    return 'couldnt_verify'
  }

  // Exclude stub entries where reason === 'not_implemented' so disabled domains
  // cannot drag a valid credential into the unknown bucket.
  const considered = Object.values(perDomain).filter(
    (entry) =>
      !(entry.state === 'unknown' && entry.reason === 'not_implemented'),
  )

  // Rule 1: any considered domain is available → credential is valid.
  if (considered.some((e) => e.state === 'available')) {
    return 'valid'
  }

  // Rule 2: any unavailable + auth_failed → invalid/expired key.
  if (
    considered.some(
      (e) => e.state === 'unavailable' && e.reason === 'auth_failed',
    )
  ) {
    return 'invalid_key'
  }

  // Rule 3: any unavailable + access_denied → missing permissions.
  if (
    considered.some(
      (e) => e.state === 'unavailable' && e.reason === 'access_denied',
    )
  ) {
    return 'missing_permissions'
  }

  // Rule 4: all remaining — all unknown, transient, cold_cache, empty.
  return 'couldnt_verify'
}

// ─── §38 presentation map ────────────────────────────────────────────────────

/**
 * Presentation metadata for each of the 6 states per brand guide §38.2.
 * Colors use AA-safe values from §38.3 (never raw text-success/warning/destructive).
 */
export interface StatusPresentation {
  /** List pill label */
  label: string
  /** Edit card headline */
  headline: string
  /** Icon to import from lucide-react */
  icon: string
  /**
   * Tailwind classes for text + border + icon color (AA-safe — §38.3).
   * These are inline-style values for dynamic colors not achievable with
   * Tailwind's static class system alone; see the component implementations
   * which apply them via `style` prop alongside structural Tailwind classes.
   */
  color: string
  /** Background tint color (hsl/hex value for inline style) */
  bgColor: string
  /** Border color for the pill / card */
  borderColor: string
  /** List action button label */
  actionLabel: string
  /** Edit card action variant for button styling */
  actionVariant: 'outline' | 'solid-red' | 'solid-amber' | 'solid-accent'
  /** Whether the action is a recovery action (routes to §37e/§37g) */
  isRecovery: boolean
}

export const STATUS_PRESENTATION: Record<DisplayStatusState, StatusPresentation> = {
  valid: {
    label: 'Valid',
    headline: 'Connection valid',
    icon: 'CheckCircle2',
    color: 'hsl(152, 69%, 38%)',
    bgColor: 'hsl(152, 69%, 96%)',
    borderColor: 'hsl(152, 50%, 85%)',
    actionLabel: 'Test connection',
    actionVariant: 'outline',
    isRecovery: false,
  },
  invalid_key: {
    label: 'Invalid key',
    headline: 'Invalid or expired key',
    icon: 'XCircle',
    color: 'hsl(0, 63%, 42%)',
    bgColor: '#FEF2F2',
    borderColor: 'hsl(0, 70%, 90%)',
    actionLabel: 'Reconnect',
    actionVariant: 'solid-red',
    isRecovery: true,
  },
  missing_permissions: {
    label: 'Missing permissions',
    headline: 'Valid, but missing required permissions',
    icon: 'ShieldAlert',
    color: 'hsl(34, 100%, 30%)',
    bgColor: '#FFFBEB',
    borderColor: 'rgba(217, 119, 6, 0.35)',
    actionLabel: 'Fix access',
    actionVariant: 'solid-amber',
    isRecovery: true,
  },
  not_tested_yet: {
    label: 'Not tested yet',
    headline: 'Connection not tested yet',
    icon: 'CircleDashed',
    color: '#64748B',
    bgColor: '#FFFFFF',
    borderColor: '#E2E8F0',
    actionLabel: 'Test connection',
    actionVariant: 'solid-accent',
    isRecovery: false,
  },
  testing: {
    label: 'Testing…',
    headline: 'Testing connection…',
    icon: 'Loader2',
    color: '#2563EB',
    bgColor: '#EFF6FF',
    borderColor: 'hsl(217, 91%, 88%)',
    actionLabel: 'Testing…',
    actionVariant: 'outline',
    isRecovery: false,
  },
  couldnt_verify: {
    label: "Couldn't verify",
    headline: "Couldn't verify the connection",
    icon: 'HelpCircle',
    color: '#475569',
    bgColor: '#F8FAFC',
    borderColor: '#E2E8F0',
    actionLabel: 'Try again',
    actionVariant: 'outline',
    isRecovery: false,
  },
}

// ─── Subcopy (edit card, reason-aware) ────────────────────────────────────────

/**
 * Verbatim subcopy per state from designs/credential-status/2026-05-29-credential-status-v1.md
 * §"Per-state action button" / "Subcopy" section. Brand guide §38.5.
 */
export const STATUS_SUBCOPY: Record<DisplayStatusState, string> = {
  valid:
    'This credential can reach all required CampMinder data. Your dashboards will sync normally.',
  invalid_key:
    'CampMinder rejected this API key. It may have expired or been revoked. Re-enter a valid key to resume syncing — your existing dashboard data stays viewable.',
  missing_permissions:
    "This key authenticates, but it wasn't created with all the data scopes TCS needs. Re-create the key in CampMinder with every scope enabled, then re-enter it here.",
  not_tested_yet:
    'Run a quick test to confirm this credential can reach CampMinder before your dashboards rely on it.',
  testing: 'Checking this credential against CampMinder. This usually takes a few seconds.',
  couldnt_verify:
    "We couldn't reach CampMinder to check this credential — this is usually a temporary hiccup on their side, not a problem with your key. Try again in a moment.",
}

// ─── Relative-time formatter ─────────────────────────────────────────────────

/**
 * Formats an ISO-8601 timestamp as a relative time string suitable for the
 * "Last tested {relative}" meta (e.g. "2 days ago", "1 hour ago", "just now").
 *
 * Returns null when the timestamp is null or unparseable (the caller should
 * omit the "Last tested" meta row entirely in that case).
 *
 * The absolute timestamp is exposed via the `title` attribute on the rendered
 * element for hover/focus (§38.8 accessibility requirement).
 */
export function formatRelativeTime(isoTimestamp: string | null | undefined): string | null {
  if (!isoTimestamp) return null
  const date = new Date(isoTimestamp)
  if (isNaN(date.getTime())) return null

  const now = Date.now()
  const diffMs = now - date.getTime()

  if (diffMs < 0) return 'just now' // future timestamps treated as "just now"

  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMinutes < 1) return 'just now'
  if (diffMinutes === 1) return '1 minute ago'
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`
  if (diffHours === 1) return '1 hour ago'
  if (diffHours < 24) return `${diffHours} hours ago`
  if (diffDays === 1) return '1 day ago'
  // Bounded relative phrasing (§38.8): beyond ~30 days, "N days ago" stops being
  // meaningful — fall back to the absolute date.
  if (diffDays > 30) return formatAbsoluteTime(isoTimestamp)
  return `${diffDays} days ago`
}

/**
 * Formats an ISO-8601 timestamp as an absolute date string for use in the
 * `title` attribute tooltip (§38.8: "Last tested" exposes absolute on hover).
 *
 * Example: "May 27, 2026, 9:14 AM"
 */
export function formatAbsoluteTime(isoTimestamp: string | null | undefined): string | null {
  if (!isoTimestamp) return null
  const date = new Date(isoTimestamp)
  if (isNaN(date.getTime())) return null
  return date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
